import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AudioCtx = createContext(null);

/** Hybrid audio player: plays real MP3 when `audio_url` is present on the name,
 *  else falls back to a synthetic Om drone generated via Web Audio API. */
export const AudioProvider = ({ children }) => {
  const [currentName, setCurrentName] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.6);
  const ctxRef = useRef(null);
  const nodesRef = useRef([]);
  const gainRef = useRef(null);
  const htmlAudioRef = useRef(null);
  const [mode, setMode] = useState("none"); // "real" | "drone" | "none"
  const queueRef = useRef({ list: [], idx: -1 });
  const [queueInfo, setQueueInfo] = useState(null);
  const playRef = useRef(null);

  const goto = (idx) => {
    const q = queueRef.current;
    if (idx < 0 || idx >= q.list.length) return false;
    q.idx = idx;
    setQueueInfo({ idx, len: q.list.length });
    playRef.current(q.list[idx], true);
    return true;
  };
  const playQueue = (list, start = 0) => {
    queueRef.current = { list, idx: -1 };
    goto(start);
  };
  const next = () => goto(queueRef.current.idx + 1);
  const prev = () => goto(queueRef.current.idx - 1);

  const ensureCtx = () => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctxRef.current = new AC();
      const g = ctxRef.current.createGain();
      g.gain.value = volume;
      g.connect(ctxRef.current.destination);
      gainRef.current = g;
    }
    return ctxRef.current;
  };

  const stop = () => {
    nodesRef.current.forEach((n) => { try { n.stop(); } catch (_) {} });
    nodesRef.current = [];
    if (htmlAudioRef.current) {
      try { htmlAudioRef.current.pause(); } catch (_) {}
    }
    setPlaying(false);
  };

  const playName = (name, fromQueue = false) => {
    if (!fromQueue) { queueRef.current = { list: [], idx: -1 }; setQueueInfo(null); }
    stop();
    // Fire-and-forget chant log for logged-in users
    if (localStorage.getItem("pd_token")) {
      api.post("/chants/log", { name_id: name?.id || null }).catch(() => {});
    }
    if (name?.audio_url) {
      // Real audio path
      if (!htmlAudioRef.current) {
        htmlAudioRef.current = new Audio();
        htmlAudioRef.current.addEventListener("ended", () => {
          const q = queueRef.current;
          if (!(q.list.length && goto(q.idx + 1))) setPlaying(false);
        });
      }
      htmlAudioRef.current.loop = !queueRef.current.list.length;
      htmlAudioRef.current.src = `${BACKEND_URL}${name.audio_url}`;
      htmlAudioRef.current.volume = volume;
      htmlAudioRef.current.play().then(() => { setPlaying(true); setMode("real"); }).catch(() => {
        // On play failure, fall back to drone
        startDrone();
        setMode("drone");
      });
      setCurrentName(name);
      return;
    }
    startDrone();
    setMode("drone");
    setCurrentName(name);
    setPlaying(true);
  };

  const startDrone = () => {
    const ctx = ensureCtx();
    const now = ctx.currentTime;
    const fundamentals = [110, 164.8, 220];
    fundamentals.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = f;
      g.gain.value = 0;
      g.gain.linearRampToValueAtTime(0.14 - i * 0.03, now + 0.8);
      osc.connect(g);
      g.connect(gainRef.current);
      osc.start(now);
      nodesRef.current.push(osc);
    });
    try {
      const lfo = ctx.createOscillator();
      const lfoG = ctx.createGain();
      lfo.frequency.value = 0.25;
      lfoG.gain.value = 2;
      lfo.connect(lfoG);
      lfoG.connect(nodesRef.current[2].frequency);
      lfo.start(now);
      nodesRef.current.push(lfo);
    } catch (_) {}
  };

  playRef.current = playName;

  const toggle = () => {
    if (playing) stop();
    else if (htmlAudioRef.current && mode === "real" && currentName?.audio_url) {
      htmlAudioRef.current.play().then(() => setPlaying(true)).catch(() => {});
    } else if (currentName) playName(currentName, Boolean(queueInfo));
  };

  useEffect(() => {
    if (gainRef.current) gainRef.current.gain.value = volume;
    if (htmlAudioRef.current) htmlAudioRef.current.volume = volume;
  }, [volume]);

  useEffect(() => () => stop(), []);

  return (
    <AudioCtx.Provider value={{ currentName, playing, volume, setVolume, playName, stop, toggle, mode, playQueue, next, prev, queueInfo }}>
      {children}
    </AudioCtx.Provider>
  );
};

export const useAudio = () => useContext(AudioCtx);
